// ============================================================
// DM-LMS — Profile Context
// Fetches the logged-in user's role-scoped profile (GET /api/profile/student,
// /api/profile/tutor, or /api/profile/admin) exactly once per mount and
// shares it with every consumer below the provider — the guard
// (RequireCompleteProfile) and the role ProfilePages all read from here
// instead of each independently calling useFetch on the same endpoint.
// ============================================================

import { createContext, useContext } from "react";
import type { ReactNode } from "react";
import { useFetch } from "../hooks/useApi.js";
import type { Role, StudentProfile, TutorProfile } from "@dm-lms/shared/types";

// GET /api/profile/tutor returns id: null in its empty shape (no row saved
// yet) — TutorProfile itself declares id as a plain (non-null) string, so
// this widens just that field to match what the API actually sends.
type TutorProfileResponse = Omit<TutorProfile, "id"> & { id: string | null; name: string; email: string; code: string };
type StudentProfileResponse = StudentProfile & { name: string; email: string; code: string };
interface AdminProfileResponse {
  id:        string;
  name:      string;
  email:     string;
  code:      string;
  createdAt: string;
  phone:     string | null;
}

interface ProfileByRole {
  student: StudentProfileResponse;
  tutor:   TutorProfileResponse;
  admin:   AdminProfileResponse;
}

const PROFILE_URL: Record<Role, string> = {
  student: "/api/profile/student",
  tutor:   "/api/profile/tutor",
  admin:   "/api/profile/admin",
};

interface ProfileContextValue<R extends Role = Role> {
  profile:   ProfileByRole[R] | null;
  isLoading: boolean;
  error:     string | null;
  refetch:   () => void;
}

const ProfileContext = createContext<ProfileContextValue | null>(null);

export function ProfileProvider({ role, children }: { role: Role; children: ReactNode }) {
  const { data, isLoading, error, refetch } = useFetch<ProfileByRole[Role]>(PROFILE_URL[role]);

  return (
    <ProfileContext.Provider value={{ profile: data, isLoading, error, refetch }}>
      {children}
    </ProfileContext.Provider>
  );
}

export function useProfile<R extends Role = Role>(): ProfileContextValue<R> {
  const ctx = useContext(ProfileContext);
  if (!ctx) throw new Error("useProfile must be used within a ProfileProvider");
  return ctx as ProfileContextValue<R>;
}
